import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import TarotCard from './TarotCard';
import CardBackDesign from './CardBackDesign';
import { tarotApi, type SpreadResponse } from '../lib/api';
import type { Persona, Theme } from '../lib/personas';

type Stage = 'selecting' | 'ready' | 'focusing' | 'revealing';
type PickFocus = { slot: number; order: number; stamp: number };

interface ReadingConsoleProps {
  sessionId: string;
  persona: Persona;
  theme: Theme;
  concern: string;
  onSpreadComplete: (spread: SpreadResponse) => void;
}

const DECK_SLOTS = Array.from({ length: 78 }, (_, i) => i);
const SPREAD_POSITIONS = ['過去', '現在', '未來'] as const;
const REVEAL_COMPLETE_MS = 5200;

const CARD_REVEAL_INTROS = [
  '先看這張牌承載的背景力量：它通常指向已經形成的情緒、習慣或前因。',
  '這張牌代表當下正在運作的核心狀態：它會指出你現在真正需要面對的位置。',
  '最後這張牌不是絕對預言，而是下一步比較可能展開的方向與提醒。',
];

const MOCK_CARDS = [
  { slug: 'the-fool', name: '愚者' },
  { slug: 'the-magician', name: '魔術師' },
  { slug: 'the-high-priestess', name: '女祭司' },
  { slug: 'the-empress', name: '皇后' },
  { slug: 'the-hermit', name: '隱士' },
  { slug: 'the-star', name: '星星' },
  { slug: 'the-world', name: '世界' },
] as const;

const SPOTLIGHT: Record<Theme, string> = {
  morandi: 'radial-gradient(circle, rgba(255,255,255,0.95), rgba(190,122,111,0.35) 45%, transparent 68%)',
  mystic: 'radial-gradient(circle, rgba(229,200,116,0.85), rgba(103,78,166,0.32) 42%, transparent 70%)',
  minimal: 'linear-gradient(90deg, transparent, rgba(10,10,10,0.16), transparent)',
};

const COPY: Record<Theme, { kicker: string; title: string; hint: string; cta: string }> = {
  morandi: {
    kicker: '柔光選牌',
    title: '憑直覺選三張牌',
    hint: '選滿後，我會把它們排成過去、現在、未來。',
    cta: '開始排牌',
  },
  mystic: {
    kicker: '儀式聚光',
    title: '讓手停在三張牌上',
    hint: '三張牌會依序進入門檻，然後翻開。',
    cta: '揭開牌面',
  },
  minimal: {
    kicker: '清晰掃描',
    title: '選三個訊號',
    hint: '系統會把它們轉成一組清楚的三段分析。',
    cta: '建立展牌',
  },
};

export default function ReadingConsole({
  sessionId,
  persona,
  theme,
  concern,
  onSpreadComplete,
}: ReadingConsoleProps) {
  const [selected, setSelected] = useState<number[]>([]);
  const [stage, setStage] = useState<Stage>('selecting');
  const [spread, setSpread] = useState<SpreadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickFocus, setPickFocus] = useState<PickFocus | null>(null);
  const copy = COPY[theme];

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggle(slot: number) {
    if (stage !== 'selecting' && stage !== 'ready') return;
    setSelected((prev) => {
      if (prev.includes(slot)) {
        if (stage === 'ready') setStage('selecting');
        setPickFocus(null);
        return prev.filter((n) => n !== slot);
      }
      if (prev.length >= 3) return prev;
      const next = [...prev, slot];
      setPickFocus({ slot, order: next.length, stamp: Date.now() });
      window.setTimeout(() => {
        setPickFocus((current) => (current?.slot === slot ? null : current));
      }, 760);
      if (next.length === 3) {
        window.setTimeout(() => {
          setStage((current) => (current === 'selecting' ? 'ready' : current));
        }, 560);
      }
      return next;
    });
  }

  function resetSelection() {
    setPickFocus(null);
    setSelected([]);
    setStage('selecting');
  }

  async function deal() {
    if (selected.length !== 3 || stage !== 'ready') return;
    setStage('focusing');
    setError(null);
    try {
      const response = await tarotApi.createSpread(sessionId, {
        spread: 'past_present_future',
        picks: selected,
        concern,
      });
      setSpread(response);
      window.setTimeout(() => setStage('revealing'), 650);
      window.setTimeout(() => {
        onSpreadComplete(response);
      }, REVEAL_COMPLETE_MS);
    } catch {
      const response = createMockSpread(selected, concern);
      setSpread(response);
      window.setTimeout(() => setStage('revealing'), 650);
      window.setTimeout(() => {
        onSpreadComplete(response);
      }, REVEAL_COMPLETE_MS);
    }
  }

  const title = stage === 'ready' ? '三張牌已選好' : copy.title;
  const hint = stage === 'ready' ? '下一步只做一件事：把三張牌送到桌面中央排開。' : copy.hint;

  return (
    <>
    <section
      data-testid="reading-console"
      data-persona={persona}
      data-theme={theme}
      className={['tarot-ambient-panel tarot-glow-border relative overflow-hidden border bg-elevated/90 p-3 shadow-card backdrop-blur-xl sm:p-4', `reading-console--${stage}`].join(' ')}
      style={{ borderColor: 'var(--color-border-hi)', borderRadius: 'var(--radius-card)' }}
    >
      <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {copy.kicker}
          </p>
          <h2 className="mt-1 font-display text-lg text-heading sm:text-xl">{title}</h2>
          <p className="mt-0.5 text-sm text-muted">{hint}</p>
        </div>
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted">
          {selected.length} / 3
        </div>
      </div>
      {stage === 'selecting' && (
        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between font-mono text-[0.56rem] uppercase tracking-[0.16em] text-muted">
            <span>Intuition window</span>
            <span>follow the pull</span>
          </div>
          <div className="tarot-instinct-bar" aria-hidden="true" />
        </div>
      )}

      <AnimatePresence mode="wait">
        {stage === 'selecting' && (
          <motion.div
            key="deck"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FanDeck
              theme={theme}
              selected={selected}
              selectedSet={selectedSet}
              onToggle={toggle}
            />

            {error && <p data-testid="spread-error" className="mt-4 text-sm text-accent">{error}</p>}
          </motion.div>
        )}

        {stage === 'ready' && (
          <motion.div
            key="ready"
            initial={{ opacity: 0, scale: 0.96, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -12 }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            <SelectedSpreadConfirm
              theme={theme}
              onConfirm={deal}
              onReset={resetSelection}
              cta={copy.cta}
            />
            {error && <p data-testid="spread-error" className="mt-4 text-center text-sm text-accent">{error}</p>}
          </motion.div>
        )}

      </AnimatePresence>

      {stage === 'revealing' && (
        <p data-testid="reveal-status" className="mt-5 text-center font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
          牌面正在翻開
        </p>
      )}
    </section>
    {createPortal(
      <AnimatePresence>
        {pickFocus && stage === 'selecting' && (
          <PickFocusOverlay key={pickFocus.stamp} theme={theme} pick={pickFocus} />
        )}
        {(stage === 'focusing' || stage === 'revealing') && (
          <RitualRevealOverlay theme={theme} spread={spread} stage={stage} selected={selected} />
        )}
      </AnimatePresence>,
      document.body,
    )}
    </>
  );
}

function createMockSpread(selected: number[], concern: string): SpreadResponse {
  const positions = ['past', 'present', 'future'] as const;
  const labels = ['過去', '現在', '未來'] as const;
  const cards = selected.slice(0, 3).map((slot, i) => {
    const card = MOCK_CARDS[slot % MOCK_CARDS.length] ?? MOCK_CARDS[0];
    return {
      slug: card.slug,
      name: card.name,
      position: positions[i],
      position_label: labels[i],
      short_reading: CARD_REVEAL_INTROS[i],
    };
  });
  const subject = concern.trim() || '你帶來的問題';
  return {
    spread: 'past_present_future',
    cards,
    overall: `這組牌先把「${subject}」拆成背景、當下與下一步。`,
    next_question: '你願意先承認哪一個部分已經足夠清楚？',
    side_context: {
      summary: '後端展牌暫時不可用，前端以展示資料完成翻牌流程。',
      chapter: '牌桌',
      signals: ['fallback', 'spread', 'demo'],
    },
    message: {
      id: `msg_spread_mock_${Date.now()}`,
      role: 'assistant',
      content: `這三張牌先給你一個可帶走的短解：\n\n**${labels[0]} / ${cards[0].name}**：${cards[0].short_reading}\n\n**${labels[1]} / ${cards[1].name}**：${cards[1].short_reading}\n\n**${labels[2]} / ${cards[2].name}**：${cards[2].short_reading}\n\n${subject} 不需要立刻被解決，先被看見就已經開始移動。`,
      cards_drawn: cards.map((card) => card.slug),
      side_context: {
        summary: '前端展示資料已完成一次三張牌解讀。',
        chapter: '牌桌',
        signals: ['fallback', 'text-only', 'cta-ready'],
      },
      created_at: Date.now() / 1000,
    },
  };
}

function FanDeck({
  theme,
  selected,
  selectedSet,
  onToggle,
}: {
  theme: Theme;
  selected: number[];
  selectedSet: Set<number>;
  onToggle: (slot: number) => void;
}) {
  const total = DECK_SLOTS.length;
  const shuffledSlots = useMemo(() => {
    const list = [...DECK_SLOTS];
    let seed = 42;
    for (let i = list.length - 1; i > 0; i -= 1) {
      seed = (seed * 1664525 + 1013904223) % 4294967296;
      const j = seed % (i + 1);
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }, []);

  return (
    <div
      className="fan-deck-panel tarot-ambient-panel tarot-glow-border relative overflow-hidden border bg-surface/40 px-3 py-3"
      style={{ borderColor: 'var(--color-border)', borderRadius: 'var(--radius-card)' }}
    >
      <div
        className="pointer-events-none absolute inset-x-[18%] top-[14%] h-[58%] opacity-70 blur-3xl"
        style={{ background: SPOTLIGHT[theme] }}
      />

      <div className="fan-deck-viewport relative z-10 overflow-hidden">
        <p className="absolute left-3 top-3 z-20 font-mono text-[0.56rem] uppercase tracking-[0.18em] text-muted">
          78-card fan deck
        </p>
        <div
          className="fan-deck-rail relative mx-auto"
          style={{ width: '100%', height: 'clamp(230px, 30vh, 340px)' }}
        >
          {shuffledSlots.map((slot, index) => {
            const ratio = total > 1 ? index / (total - 1) : 0;
            const angle = -31 + ratio * 62;
            const left = 7 + ratio * 86;
            const fanLift = -Math.sin(Math.PI * ratio) * 88;
            const picked = selectedSet.has(slot);
            const pickedIndex = selected.indexOf(slot);
            const totalLift = fanLift + (picked ? -22 : 0);

            return (
              <motion.button
                data-testid={`deck-card-${slot}`}
                key={slot}
                type="button"
                onClick={() => onToggle(slot)}
                className={[
                  'group tarot-shape-spotlight-host absolute bottom-5 aspect-[3/5] overflow-hidden shadow-sm outline-none',
                  picked ? 'z-[120]' : 'z-10',
                  picked ? 'is-active' : '',
                  picked ? 'pointer-events-none' : '',
                ].join(' ')}
                style={{
                  left: `${left}%`,
                  width: 'clamp(34px, 4.6vw, 64px)',
                  zIndex: picked ? 120 : index + 1,
                  borderRadius: 'var(--radius-card)',
                  transformOrigin: '50% 100%',
                }}
                initial={{ opacity: 0, x: '-50%', y: 44, rotate: angle * 0.3 }}
                animate={{ opacity: 1, x: '-50%', y: totalLift, rotate: angle }}
                whileHover={picked ? { y: totalLift - 10, scale: 1.05 } : { y: totalLift - 16, scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 150, damping: 20, delay: Math.min(index * 0.006, 0.22) }}
              >
                <CardBackDesign theme={theme} selected={picked} />
                <span className={['tarot-card-spotlight', picked ? 'is-active' : ''].join(' ')} />
                <span
                  className="pointer-events-none absolute inset-[-40%] opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
                  style={{ background: SPOTLIGHT[theme] }}
                />
                {theme === 'minimal' && (
                  <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 bg-white/50 opacity-0 blur-sm transition-all duration-500 group-hover:left-full group-hover:opacity-100" />
                )}
                <span className="relative flex h-full items-center justify-center">
                  <span className="font-display text-lg" style={{ color: cardInk(theme) }}>
                    {picked ? pickedIndex + 1 : ''}
                  </span>
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SelectedSpreadConfirm({
  theme,
  onConfirm,
  onReset,
  cta,
}: {
  theme: Theme;
  onConfirm: () => void;
  onReset: () => void;
  cta: string;
}) {
  return (
    <div className="spread-confirm-panel">
      <div className="spread-confirm-panel__cards">
        {SPREAD_POSITIONS.map((position, i) => (
          <motion.div
            key={position}
            className="spread-confirm-panel__card tarot-shape-spotlight-host tarot-glow-border is-active"
            style={{ borderColor: 'var(--color-accent)', borderRadius: 'var(--radius-card)' }}
            initial={{ opacity: 0.62, y: 26, rotate: (i - 1) * 7, scale: 0.84 }}
            animate={{ opacity: 1, y: 0, rotate: (i - 1) * 3, scale: 1 }}
            transition={{ type: 'spring', stiffness: 130, damping: 16, delay: i * 0.08 }}
          >
            <div className="spread-confirm-panel__back" style={{ borderRadius: 'var(--radius-card)' }}>
              <CardBackDesign theme={theme} selected />
              <span className="tarot-card-spotlight is-active" />
              <span className="spread-confirm-panel__order">{i + 1}</span>
            </div>
            <p>
              <span>{position}</span>
              <em>已選定</em>
            </p>
          </motion.div>
        ))}
      </div>

      <div className="spread-confirm-panel__actions">
        <button
          data-testid="deal-spread"
          type="button"
          onClick={onConfirm}
          className="tarot-shape-spotlight-host is-active px-6 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--btn-shadow)',
          }}
        >
          {cta}
        </button>
        <button
          data-testid="reselect-cards"
          type="button"
          onClick={onReset}
          className="px-5 py-3 text-sm font-medium text-muted transition-colors hover:text-heading"
        >
          重新選牌
        </button>
      </div>
    </div>
  );
}

function cardInk(theme: Theme): string {
  if (theme === 'mystic') return '#e8dcb9';
  if (theme === 'morandi') return '#fff';
  return '#0a0a0a';
}

function PickFocusOverlay({ theme, pick }: { theme: Theme; pick: PickFocus }) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 z-[75] flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.16 } }}
    >
      <motion.div
        className="absolute inset-0 bg-bg/34 backdrop-blur-sm"
        initial={{ backdropFilter: 'blur(0px)', scale: 1 }}
        animate={{ backdropFilter: 'blur(8px)', scale: 1.015 }}
        transition={{ duration: 0.18 }}
      />
      <motion.div
        className="absolute h-[18rem] w-[18rem] rounded-[40%] blur-3xl"
        style={{ background: SPOTLIGHT[theme] }}
        initial={{ scale: 0.72, opacity: 0 }}
        animate={{ scale: [0.72, 1.12, 0.94], opacity: [0, 0.82, 0.28] }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      />
      <motion.div
        className="relative aspect-[3/5] w-[118px] overflow-hidden sm:w-[156px]"
        style={{ borderRadius: 'var(--radius-card)' }}
        initial={{ y: 52, scale: 0.42, rotate: -10, opacity: 0 }}
        animate={{ y: [52, -8, 0], scale: [0.42, 1.2, 0.96], rotate: [-10, 2, 0], opacity: [0, 1, 0.92] }}
        exit={{ y: -30, scale: 0.72, opacity: 0 }}
        transition={{ duration: 0.72, ease: [0.16, 1, 0.3, 1] }}
      >
        <CardBackDesign theme={theme} selected />
        <span className="tarot-card-spotlight is-active" />
        <span className="relative flex h-full items-center justify-center">
          <span className="font-display text-4xl" style={{ color: cardInk(theme) }}>
            {pick.order}
          </span>
        </span>
      </motion.div>
      <motion.div
        className="absolute bottom-[18%] rounded-full border bg-elevated/72 px-5 py-2 text-center backdrop-blur-xl"
        style={{ borderColor: 'var(--color-border-hi)' }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: [0, 1, 0], y: [12, 0, -4] }}
        transition={{ duration: 0.72, ease: 'easeOut' }}
      >
        <p className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-accent">
          Card {pick.order} locked
        </p>
      </motion.div>
    </motion.div>
  );
}

function RitualRevealOverlay({
  theme,
  spread,
  stage,
  selected,
}: {
  theme: Theme;
  spread: SpreadResponse | null;
  stage: 'focusing' | 'revealing';
  selected: number[];
}) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const cards = spread?.cards ?? selected.map((slot, i) => ({
    slug: 'the-fool',
    name: '',
    position: i === 0 ? 'past' : i === 1 ? 'present' : 'future',
    position_label: i === 0 ? '過去' : i === 1 ? '現在' : '未來',
    short_reading: '',
    slot,
  }));
  const visibleCards = cards.slice(0, 3);
  const isFinale = stage === 'revealing' && activeIndex >= 3;
  const activeCard = stage === 'revealing' && activeIndex >= 0 && activeIndex < 3
    ? visibleCards[activeIndex]
    : null;
  const activeIntro = activeCard
    ? activeCard.short_reading || CARD_REVEAL_INTROS[activeIndex]
    : '';
  const activeTitle = activeCard
    ? activeCard.name || `第 ${activeIndex + 1} 張牌`
    : '';

  useEffect(() => {
    if (stage !== 'revealing') {
      setActiveIndex(-1);
      return;
    }
    const timers = [
      window.setTimeout(() => setActiveIndex(0), 120),
      window.setTimeout(() => setActiveIndex(1), 1320),
      window.setTimeout(() => setActiveIndex(2), 2520),
      window.setTimeout(() => setActiveIndex(3), 4080),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [stage]);

  return (
    <motion.div
      key="ritual-overlay"
      className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.25 } }}
    >
      <motion.div
        className="absolute inset-0 bg-bg/60 backdrop-blur-md"
        initial={{ backdropFilter: 'blur(0px)', scale: 1 }}
        animate={{ backdropFilter: 'blur(14px)', scale: stage === 'revealing' ? 1.03 : 1.018 }}
        transition={{ duration: 0.34 }}
      />
      <motion.div
        className="absolute inset-[14%] rounded-[48%] opacity-70 blur-3xl"
        style={{
          background:
            theme === 'mystic'
              ? 'radial-gradient(ellipse at center, rgba(201,162,74,0.34), transparent 68%)'
              : theme === 'morandi'
              ? 'radial-gradient(ellipse at center, rgba(184,122,111,0.3), transparent 68%)'
            : 'radial-gradient(ellipse at center, rgba(138,42,42,0.2), transparent 68%)',
        }}
        animate={{ scale: isFinale ? 0.85 : stage === 'revealing' ? [1.02, 1.18, 1.08] : 0.9, opacity: isFinale ? 0.42 : stage === 'revealing' ? 0.9 : 0.58 }}
        transition={{ duration: stage === 'revealing' ? 1.1 : 0.45, ease: 'easeInOut' }}
      />
      <motion.div
        className="tarot-ritual-ring"
        initial={{ scale: 0.72, opacity: 0 }}
        animate={{ scale: isFinale ? 0.78 : stage === 'revealing' ? 1 : 0.82, opacity: isFinale ? 0.45 : stage === 'revealing' ? 1 : 0.58 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      />
      <AnimatePresence>
        {stage === 'focusing' && <UnusedDeckSweep theme={theme} />}
      </AnimatePresence>

      <motion.div
        className="relative h-[282px] w-[min(90vw,620px)] sm:h-[370px]"
        initial={{ scale: 0.68, y: 58 }}
        animate={{ scale: isFinale ? 0.76 : stage === 'revealing' ? 1.08 : 0.92, y: isFinale ? 8 : 0 }}
        exit={{ scale: 0.76, y: -46, transition: { duration: 0.28 } }}
        transition={{ type: 'spring', stiffness: 128, damping: 18 }}
      >
        {visibleCards.map((card, i) => {
          const isActive = stage === 'revealing' && activeIndex === i;
          const hasRevealed = stage === 'revealing' && activeIndex >= i;
          const hasPassed = stage === 'revealing' && activeIndex > i && activeIndex < 3;
          const x = isFinale
            ? (i - 1) * 118
            : isActive
              ? 0
              : (i - 1) * (stage === 'revealing' ? 152 : 86);
          const y = isActive ? -18 : isFinale ? 18 : hasPassed ? 28 : 0;
          const scale = isActive ? 1.34 : isFinale ? 0.9 : hasPassed ? 0.72 : stage === 'revealing' ? 0.86 : 0.98;
          return (
            <motion.div
              data-testid={`spread-position-${i}`}
              key={`${card.position}-${i}`}
              initial={{
                opacity: 0,
                x: (i - 1) * 180,
                y: 150,
                scale: 0.44,
                rotate: i === 0 ? -12 : i === 2 ? 12 : 0,
              }}
              animate={{
                opacity: hasRevealed || stage === 'focusing' || isFinale ? 1 : 0.45,
                x,
                y,
                scale,
                rotate: isActive ? 0 : isFinale ? (i - 1) * 3 : i === 0 ? -5 : i === 2 ? 5 : 0,
                filter: isActive ? 'drop-shadow(0 0 36px var(--color-accent))' : hasPassed ? 'saturate(0.88) brightness(0.82)' : 'none',
              }}
              exit={{
                opacity: 0,
                x: (i - 1) * 92,
                y: -90,
                scale: 0.58,
                transition: { duration: 0.32, delay: i * 0.04 },
              }}
              transition={{ type: 'spring', stiffness: isActive ? 150 : 118, damping: 18, delay: stage === 'focusing' ? i * 0.08 : 0 }}
              className="absolute left-1/2 top-1/2 w-[98px] sm:w-[142px]"
              style={{ zIndex: isActive ? 30 : hasPassed ? 16 : 12 + i }}
            >
              <div className="relative -translate-x-1/2 -translate-y-1/2">
                <TarotCard
                  slug={card.slug}
                  theme={theme}
                  revealed={hasRevealed}
                  delay={isActive ? 130 : 0}
                  flipMs={390}
                  glow={isActive || isFinale}
                  showTitle={false}
                  position={hasRevealed ? card.position_label : undefined}
                />
                {isActive && (
                  <motion.span
                    className="pointer-events-none absolute inset-[-16%] rounded-[inherit] opacity-70 blur-xl"
                    style={{ background: SPOTLIGHT[theme] }}
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: [0.7, 1.18, 0.92], opacity: [0, 0.86, 0.2] }}
                    transition={{ duration: 0.72, ease: 'easeOut' }}
                  />
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <motion.div
        className="absolute left-1/2 top-[11%] w-[min(86vw,520px)] -translate-x-1/2 text-center"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <p className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-accent">
          {stage === 'focusing'
            ? 'collecting the deck'
            : isFinale
              ? 'spread complete'
              : activeIndex < 0
                ? 'centering cards'
              : `card ${activeIndex + 1} reveal`}
        </p>
        <h2 className="mt-2 font-display text-2xl text-heading sm:text-4xl">
          {stage === 'focusing'
            ? '把三張牌移到桌心'
            : isFinale
              ? '三張牌回到展牌位置'
              : activeIndex < 0
                ? '準備翻牌'
                : '單張牌義正在浮現'}
        </h2>
      </motion.div>

      <AnimatePresence mode="wait">
        {activeCard && (
          <motion.div
            key={`reading-${activeIndex}`}
            className="absolute bottom-[8%] left-1/2 w-[min(88vw,560px)] -translate-x-1/2 border bg-elevated/78 px-5 py-4 text-center shadow-card backdrop-blur-2xl sm:px-6"
            style={{ borderColor: 'var(--color-border-hi)', borderRadius: 'var(--radius-card)' }}
            initial={{ opacity: 0, y: 22, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96, transition: { duration: 0.18 } }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent">
              {activeCard.position_label} · 單張牌義
            </p>
            <h2 className="mt-1 font-display text-xl text-heading sm:text-2xl">{activeTitle}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted sm:text-base">
              {activeIntro}
            </p>
          </motion.div>
        )}
        {isFinale && (
          <motion.div
            key="reading-finale"
            className="absolute bottom-[10%] left-1/2 w-[min(86vw,460px)] -translate-x-1/2 border bg-elevated/72 px-5 py-3 text-center backdrop-blur-2xl"
            style={{ borderColor: 'var(--color-border-hi)', borderRadius: 'var(--radius-pill)' }}
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-accent">
              回到牌桌 · 開始完整解讀
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function UnusedDeckSweep({ theme }: { theme: Theme }) {
  const deck = Array.from({ length: 16 }, (_, i) => i);
  return (
    <motion.div
      className="pointer-events-none absolute inset-0 z-[1]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.22 } }}
    >
      {deck.map((item) => {
        const side = item % 2 === 0 ? -1 : 1;
        const row = item % 4;
        const top = 24 + row * 12 + Math.floor(item / 4) * 3;
        return (
          <motion.div
            key={item}
            className="absolute aspect-[3/5] w-[38px] overflow-hidden opacity-70 sm:w-[48px]"
            style={{ top: `${top}%`, left: '50%', borderRadius: 'var(--radius-card)' }}
            initial={{ x: -19, y: 60, rotate: side * (8 + row * 4), scale: 0.72, opacity: 0 }}
            animate={{
              x: side * (260 + item * 12),
              y: -42 + row * 18,
              rotate: side * (34 + item * 2),
              scale: [0.72, 0.86, 0.52],
              opacity: [0, 0.62, 0],
            }}
            transition={{ duration: 0.78, delay: item * 0.018, ease: [0.22, 0.8, 0.24, 1] }}
          >
            <CardBackDesign theme={theme} />
          </motion.div>
        );
      })}
      <motion.div
        className="absolute left-1/2 top-[48%] w-[min(78vw,420px)] -translate-x-1/2 rounded-full border bg-elevated/62 px-5 py-2 text-center backdrop-blur-2xl"
        style={{ borderColor: 'var(--color-border-hi)' }}
        initial={{ opacity: 0, y: 18, scale: 0.94 }}
        animate={{ opacity: [0, 1, 0.92], y: [18, 0, -2], scale: [0.94, 1.02, 1] }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.56, ease: 'easeOut' }}
      >
        <p className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-accent">
          未選牌收起 · 三張牌進入聚焦
        </p>
      </motion.div>
    </motion.div>
  );
}
